import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, X, Link2, ClipboardCheck, AlertTriangle, BarChart3, Target } from "lucide-react";

export const SOURCE_LABEL = { audit_finding: "Audit-fund", reklamation: "Reklamation", complaint_analysis: "Analyse", goal: "Mål" };
const SOURCE_ICON = { audit_finding: ClipboardCheck, reklamation: AlertTriangle, complaint_analysis: BarChart3, goal: Target };
const TABS = [["", "Alle"], ["audit_finding", "Audit-fund"], ["reklamation", "Reklamationer"], ["complaint_analysis", "Analyser"], ["goal", "Mål"]];

function SourceRow({ s, onPick, testid }) {
  const Icon = SOURCE_ICON[s.type] || Link2;
  return (
    <button type="button" onClick={onPick} className="w-full text-left px-3 py-2 hover:bg-[#FBFAF8] text-sm" data-testid={testid}>
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="h-3.5 w-3.5 text-[#806B61] shrink-0" />
        <span className="text-[11px] text-[#806B61] shrink-0">{SOURCE_LABEL[s.type]}</span>
        {s.no && <span className="font-mono text-xs text-stone-500 shrink-0">{s.no}</span>}
        <span className="truncate text-stone-800 flex-1">{s.title || "—"}</span>
      </div>
      <div className="text-[11px] text-stone-500 pl-5.5 ml-[22px] truncate">{[s.date ? daDate(s.date) : null, s.status, s.sub].filter(Boolean).join(" · ")}</div>
    </button>
  );
}

export function SourcePicker({ value, onChange, meetingId, testid = "src-picker" }) {
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => api.get("/le/sources/search", { params: { q, meeting_id: meetingId || undefined } }).then((r) => setRes(r.data)).catch(() => setRes([])), 250);
    return () => clearTimeout(t);
  }, [q, open, meetingId]);
  const has = (s) => value.some((v) => v.type === s.type && v.id === s.id);
  const shown = res.filter((s) => !has(s) && (!tab || s.type === tab));
  return (
    <div data-testid={testid}>
      {value.length > 0 && <div className="flex flex-col gap-1.5 mb-2">{value.map((s) => { const Icon = SOURCE_ICON[s.type] || Link2; return (
        <span key={`${s.type}-${s.id}`} className="flex items-start gap-1.5 text-xs rounded-xl border border-[#EBE8E3] bg-[#FBFAF8] pl-2.5 pr-1 py-1.5" data-testid={`${testid}-chip-${s.id}`}>
          <Icon className="h-3.5 w-3.5 text-[#806B61] shrink-0 mt-0.5" />
          <span className="flex-1 min-w-0"><span className="text-stone-500">{SOURCE_LABEL[s.type]}</span>{s.no ? <span className="font-mono ml-1">{s.no}</span> : null}<span className="block text-stone-800 break-words">{s.title}</span></span>
          {!s.locked && <button type="button" onClick={() => onChange(value.filter((v) => !(v.type === s.type && v.id === s.id)))} className="rounded-full p-1 hover:bg-stone-200 shrink-0" data-testid={`${testid}-remove-${s.id}`}><X className="h-3 w-3" /></button>}
        </span>
      ); })}</div>}
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-3 text-stone-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setOpen(true)} placeholder="Søg nr., titel, varenr. eller dato…" className="rounded-xl pl-9" data-testid={`${testid}-search`} />
      </div>
      {open && (
        <div className="mt-1.5 rounded-xl border border-[#EBE8E3] bg-white overflow-hidden" data-testid={`${testid}-results`}>
          <div className="flex gap-1 p-1.5 border-b border-[#F0EDE8] overflow-x-auto">{TABS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} className={`px-2.5 py-1 rounded-lg text-xs whitespace-nowrap ${tab === k ? "bg-[#A83232] text-white" : "text-stone-600 hover:bg-[#F3F1EC]"}`} data-testid={`${testid}-tab-${k || "all"}`}>{l}</button>
          ))}</div>
          {!q.trim() && shown.length > 0 && <p className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wide text-[#806B61]">Foreslået</p>}
          <div className="max-h-56 overflow-y-auto divide-y divide-[#F0EDE8]">
            {shown.map((s) => <SourceRow key={`${s.type}-${s.id}`} s={s} onPick={() => { onChange([...value, s]); setQ(""); }} testid={`${testid}-opt-${s.id}`} />)}
            {shown.length === 0 && <p className="px-3 py-2 text-xs text-stone-400">{q.trim() ? "Ingen resultater" : "Ingen forslag — søg ovenfor"}</p>}
          </div>
          <button type="button" onClick={() => setOpen(false)} className="w-full text-xs text-stone-500 py-1.5 hover:bg-[#FBFAF8] border-t border-[#F0EDE8]">Luk</button>
        </div>
      )}
    </div>
  );
}

export function MeetingPointPicker({ meetingId, agendaKey, onChange, lockMeeting = false, testid = "origin-picker" }) {
  const [meetings, setMeetings] = useState([]);
  const [agenda, setAgenda] = useState([]);
  useEffect(() => { if (!lockMeeting) api.get("/le/meetings").then((r) => setMeetings(r.data)).catch(() => {}); }, [lockMeeting]);
  useEffect(() => {
    if (!meetingId) { setAgenda([]); return; }
    api.get(`/le/meetings/${meetingId}`).then((r) => setAgenda(r.data.agenda || [])).catch(() => setAgenda([]));
  }, [meetingId]);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {!lockMeeting && (
        <select value={meetingId || ""} onChange={(e) => onChange({ meetingId: e.target.value || null, agendaKey: null })} className="h-10 px-2.5 rounded-xl border border-[#EBE8E3] bg-white text-sm" data-testid={`${testid}-meeting`}>
          <option value="">Vælg LE-møde…</option>
          {meetings.map((m) => <option key={m.id} value={m.id}>{daDate(m.date)} · {m.title}</option>)}
        </select>
      )}
      <select value={agendaKey || ""} disabled={!meetingId} onChange={(e) => onChange({ meetingId, agendaKey: e.target.value || null })} className="h-10 px-2.5 rounded-xl border border-[#EBE8E3] bg-white text-sm disabled:opacity-50" data-testid={`${testid}-point`}>
        <option value="">Vælg punkt…</option>
        {agenda.map((it, i) => <option key={it.id} value={it.key}>pkt. {i + 1} · {it.title}</option>)}
      </select>
    </div>
  );
}

export function OriginFields({ mode, setMode, meetingId, agendaKey, setPoint, sources, setSources, lockMeeting, testid = "origin" }) {
  return (
    <div className="rounded-xl border border-[#EBE8E3] p-3 space-y-3" data-testid={`${testid}-fields`}>
      <p className="text-xs font-medium text-stone-500">Kommer fra</p>
      <div className="flex flex-wrap gap-1 rounded-xl border border-[#EBE8E3] p-1 text-sm">
        <button type="button" data-testid={`${testid}-mode-meeting`} onClick={() => setMode("meeting")} className={`px-3 py-1.5 rounded-lg ${mode === "meeting" ? "bg-[#A83232] text-white" : "text-stone-600"}`}>LE-møde og punkt</button>
        <button type="button" data-testid={`${testid}-mode-between`} onClick={() => setMode("between")} className={`px-3 py-1.5 rounded-lg ${mode === "between" ? "bg-[#A83232] text-white" : "text-stone-600"}`}>Godkendt mellem møder</button>
      </div>
      {mode === "meeting" && <MeetingPointPicker meetingId={meetingId} agendaKey={agendaKey} onChange={setPoint} lockMeeting={lockMeeting} testid={testid} />}
      <div>
        <p className="text-xs text-stone-500 mb-1.5">Kilder (0..n)</p>
        <SourcePicker value={sources} onChange={setSources} meetingId={meetingId} testid={`${testid}-sources`} />
      </div>
    </div>
  );
}

export function AttachDialog({ action, onClose, onSaved }) {
  const o = action.origin || {};
  const [mode, setMode] = useState(o.missing_point ? "meeting" : "between");
  const [meetingId, setMeetingId] = useState(action.origin_meeting_id || null);
  const [agendaKey, setAgendaKey] = useState(action.origin_agenda_key || null);
  const [sources, setSources] = useState([]);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = { sources: sources.map((s) => ({ type: s.type, id: s.id })) };
      if (mode === "meeting") { if (!action.origin_meeting_id && meetingId) body.origin_meeting_id = meetingId; if (!action.origin_agenda_key && agendaKey) body.origin_agenda_key = agendaKey; }
      const r = await api.post(`/le/actions/${action.id}/attach`, body);
      toast.success("Kobling tilknyttet"); onSaved?.(r.data); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl max-w-lg w-[calc(100vw-1.5rem)] sm:w-full max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="attach-dialog">
        <DialogHeader><DialogTitle>Tilknyt · {action.action_no}</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-600">{action.description}</p>
        <p className="text-xs text-stone-500">Nuværende: {o.line || "—"}. Kun tomme felter udfyldes — eksisterende koblinger ændres ikke.</p>
        <OriginFields mode={mode} setMode={setMode} meetingId={meetingId} agendaKey={agendaKey} lockMeeting={!!action.origin_meeting_id}
          setPoint={({ meetingId: m, agendaKey: k }) => { setMeetingId(m); setAgendaKey(k); }} sources={sources} setSources={setSources} testid="attach" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Annullér</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="attach-save"><Link2 className="h-4 w-4 mr-1.5" /> {saving ? "Gemmer…" : "Tilknyt"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
